import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Input } from '../components/ui'
import { GatedButton } from '../components/ui/GatedButton'
import { StepChips } from '../components/ui/StepChips'
import { useToast } from '../contexts/ToastContext'
import { usePerson } from '../contexts/PersonContext'
import { ageFromDob } from '../lib/age'
import { cn } from '../lib/cn'
import { onboardChildConsentText } from '../lib/legal/cotNotices'
import { createPerson } from '../lib/api/persons'
import { useQuery } from '@tanstack/react-query'
import { getVerification } from '../lib/api/verification'
import { hasCurrentVerification } from '../lib/verificationStatus'
import type { Supervision } from '../hooks/useSignupWizard'

const STEPS = ['Who they are', 'Date of birth', 'Verify you are their parent', 'Supervision', 'Done']

const SUP_CHOICES: Array<{ key: Supervision; label: string; body: string }> = [
  { key: 'strict', label: 'Approve everything', body: 'Every friend request, circle join and room invite comes to you first. Required under 13.' },
  { key: 'view', label: 'View only', body: 'They can browse but cannot send anything. You send on their behalf.' },
  { key: 'self', label: 'They send their own requests', body: 'You still see everything and every report. Only available at 13 and over.' },
]

type Tone = 'neutral' | 'positive' | 'warn'
function toneBox(tone: Tone): string {
  return tone === 'positive' ? 'bg-moss-panel text-moss' : tone === 'warn' ? 'bg-honey-bg text-honey-fg' : 'bg-paper-panel text-ink-muted'
}

interface OnbState {
  step: number
  first: string
  last: string
  dob: string
  supervision: Supervision
  consent: boolean
}

const fresh = (): OnbState => ({ step: 1, first: '', last: '', dob: '', supervision: 'strict', consent: false })

/**
 * Add-person onboarding (COTC_SignupConsent R7). 5 steps verbatim from the design; children's-data
 * invariants (exact DOB, under-13 approve-everything lock, consent before creation) are re-enforced
 * server-side by POST /persons.
 */
export function OnboardPersonPage() {
  const navigate = useNavigate()
  const { flash } = useToast()
  const { refresh } = usePerson()
  const [s, setS] = useState<OnbState>(fresh)
  const [busy, setBusy] = useState(false)
  const verification = useQuery({ queryKey: ['community', 'verification'], queryFn: getVerification, retry: false, staleTime: 0 })
  const verified = !verification.isError && hasCurrentVerification(verification.data)

  const patch = (p: Partial<OnbState>) => setS((prev) => ({ ...prev, ...p }))

  const age = ageFromDob(s.dob)
  const under13 = age !== null && age < 13
  const consentText = onboardChildConsentText(s.first)

  const gate: Record<number, boolean> = {
    1: s.first.trim().length > 0,
    2: age !== null,
    3: verified && s.consent,
    4: verified && s.consent,
  }
  const hint: Record<number, string> = {
    1: 'A first name is enough, but we need one.',
    2: 'Enter a valid date of birth.',
    3: 'Verify and tick the consent box to continue.',
    4: '',
  }

  async function create() {
    if (busy || !verified || !hasCurrentVerification(verification.data) || !s.consent) return
    setBusy(true)
    try {
      await createPerson({
        firstName: s.first.trim(),
        surname: s.last.trim() || undefined,
        dateOfBirth: s.dob,
        supervision: s.supervision,
        consentMethod: 'card',
        consentText,
      })
      refresh()
      flash(`Profile created for ${s.first.trim()}.`)
      patch({ step: 5 })
    } catch (err) {
      flash(err instanceof Error ? err.message : 'Could not create the profile.')
    } finally {
      setBusy(false)
    }
  }

  function next() {
    if (s.step === 4) {
      void create()
      return
    }
    if (!gate[s.step]) {
      flash(hint[s.step])
      return
    }
    patch({ step: s.step + 1 })
  }

  const ageNote = age === null
    ? 'We ask for a date of birth, not an age band. Under COPPA we have to know, and we are not allowed to guess.'
    : under13
      ? 'Under 13. COPPA applies: we will collect verifiable parental consent before anything is created, share nothing publicly, and keep only what the service needs.'
      : '13 or over. COPPA no longer applies, but this account stays parent-managed — that is our rule, not the law’s.'
  const ageTone: Tone = age === null ? 'neutral' : under13 ? 'warn' : 'positive'

  return (
    <div className="animate-rise px-10 py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-fainter">Add a person</div>
      <h1 className="mt-2 font-serif text-[30px] font-medium tracking-[-.015em] text-ink">Set up a profile you manage</h1>
      <p className="mt-3 max-w-[56ch] text-[13.5px] leading-relaxed text-ink-muted">Children cannot sign themselves up — you create and manage their profile.</p>

      <div className="mt-6">
        <StepChips steps={STEPS} current={s.step} />
      </div>

      <div className="mt-6 max-w-[560px] rounded-card border border-line-soft bg-paper-surface p-7 shadow-card">
        {s.step === 1 && (
          <div className="grid gap-4">
            <Input label="First name (all other families see this)" value={s.first} onChange={(e) => patch({ first: e.target.value })} placeholder="First name" />
            <Input label="Private surname (optional — never shown to other families)" value={s.last} onChange={(e) => patch({ last: e.target.value })} placeholder="Optional" />
          </div>
        )}

        {s.step === 2 && (
          <div>
            <Input label="Exact date of birth" type="date" value={s.dob} onChange={(e) => patch({ dob: e.target.value, supervision: 'strict', consent: false })} />
            {age !== null && <div className="mt-3 font-serif text-[18px] text-ink">{age} years old</div>}
            <div className={cn('mt-3 rounded-input px-4 py-3 text-[12.5px] leading-relaxed', toneBox(ageTone))}>{ageNote}</div>
          </div>
        )}

        {s.step === 3 && (
          <div>
            <div className="rounded-input bg-paper-panel p-4 text-sm leading-relaxed text-ink-muted" aria-live="polite">
              {verification.isPending ? 'Checking parent verification…' : verified ? 'Parent accountability verified by card payment.' : 'Current parent payment verification is required before a profile can be created.'}
              {verification.data?.testMode && <p className="mt-2 text-honey-fg">Test mode — no live payment is verified here.</p>}
              {!verified && <Link to="/verification" className="mt-3 block text-terra-deep underline">Open parent verification</Link>}
              {verification.isError && <p role="alert">Could not check verification. <button type="button" className="underline" onClick={() => void verification.refetch()}>Try again</button></p>}
            </div>

            <button
              type="button"
              onClick={() => patch({ consent: !s.consent })}
              aria-pressed={s.consent}
              className={cn('mt-4 flex w-full items-start gap-3 rounded-input border p-4 text-left', s.consent ? 'border-terra bg-paper-panel' : 'border-line-soft')}
            >
              <span className={cn('mt-[2px] flex h-5 w-5 flex-none items-center justify-center rounded-full border text-[11px] text-paper-surface', s.consent ? 'border-moss bg-moss' : 'border-line')}>
                {s.consent ? '✓' : ''}
              </span>
              <span className="text-[13px] leading-relaxed text-ink">{consentText}</span>
            </button>
          </div>
        )}

        {s.step === 4 && (
          <div className="grid gap-3">
            {SUP_CHOICES.map((c) => {
              const locked = c.key === 'self' && under13
              const on = s.supervision === c.key
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={on}
                  aria-disabled={locked}
                  onClick={() => (locked ? flash('Under 13 must have a parent approve everything.') : patch({ supervision: c.key }))}
                  className={cn('rounded-input border p-4 text-left transition-colors', on ? 'border-terra bg-paper-panel' : 'border-line-soft', locked && 'opacity-60')}
                >
                  <div className={cn('font-serif text-[15px]', locked ? 'text-ink-fainter' : 'text-ink')}>{c.label}</div>
                  <div className="mt-1 text-[12px] leading-relaxed text-ink-muted">
                    {c.body}
                    {locked ? ` Locked — they are ${age}.` : ''}
                  </div>
                </button>
              )
            })}
          </div>
        )}

        {s.step === 5 && (
          <div className="rounded-card bg-moss-panel p-6 text-center">
            <div className="font-serif text-[22px] font-medium text-ink">{s.first.trim()} is set up.</div>
            <p className="mx-auto mt-2 max-w-[58ch] text-[13.5px] leading-relaxed text-ink-muted">
              {(s.first.trim() || 'They') + ' can sign in now. '}
              {under13
                ? 'Because they are under 13, nothing they send leaves your approval queue, and their profile is invisible outside your circles.'
                : 'Their profile is visible only to families in circles you both belong to.'}
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={() => navigate('/')} className="rounded-btn bg-moss px-[18px] py-3 text-[13.5px] font-medium text-paper-surface">
                Back to home
              </button>
              <button type="button" onClick={() => setS(fresh())} className="rounded-btn border border-moss-border bg-transparent px-[18px] py-3 text-[13.5px] font-medium text-moss">
                Add another person
              </button>
            </div>
          </div>
        )}

        {s.step < 5 && (
          <div className="mt-7 flex items-center gap-4 border-t border-line-row pt-5">
            <p className="min-w-0 flex-1 text-[12.5px] leading-relaxed text-ink-faint">{hint[s.step]}</p>
            {s.step > 1 && (
              <button
                type="button"
                onClick={() => patch({ step: Math.max(1, s.step - 1) })}
                className="rounded-btn border border-line px-[18px] py-3 text-[13.5px] font-medium text-ink-muted hover:border-terra hover:text-terra-deep"
              >
                Back
              </button>
            )}
            <GatedButton gated={busy || !(gate[s.step] ?? true)} hint={hint[s.step]} onClick={next}>
              {busy ? 'Working…' : s.step === 4 ? 'Create profile' : 'Continue'}
            </GatedButton>
          </div>
        )}
      </div>
    </div>
  )
}
